var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { eventBus, } from "@/context/shared/eventBus";
import { useNotification } from "./useNotification";
/**
 * Компонент для автоматического отображения ошибок API и общих уведомлений
 */
export var ApiErrorHandler = function (_a) {
    var children = _a.children;
    var _b = useNotification(), showError = _b.showError, showWarning = _b.showWarning, showInfo = _b.showInfo, showSuccess = _b.showSuccess;
    React.useEffect(function () {
        // Слушаем глобальные события ошибок API через mitt
        var handleApiError = function (data) {
            var _a;
            var error = data.error, context = data.context;
            var message = "Произошла ошибка при выполнении запроса";
            var title = "Ошибка API";
            var isWarning = false;
            if (error && typeof error === "object" && "status" in error) {
                var apiError = error;
                switch (apiError.status) {
                    case 400:
                        message = "Некорректные данные запроса";
                        title = "Ошибка валидации";
                        break;
                    case 401:
                        message = "Необходима авторизация";
                        title = "Требуется вход в систему";
                        break;
                    case 403:
                        message = "Недостаточно прав для выполнения операции";
                        title = "Доступ запрещен";
                        break;
                    case 404:
                        message = "Запрашиваемый ресурс не найден";
                        title = "Ресурс не найден";
                        isWarning = true;
                        break;
                    case 409:
                        message = "Конфликт данных. Возможно, ресурс уже существует";
                        title = "Конфликт данных";
                        isWarning = true;
                        break;
                    case 422:
                        message = "Данные не прошли валидацию на сервере";
                        title = "Ошибка валидации";
                        break;
                    case 429:
                        message = "Слишком много запросов. Попробуйте позже";
                        title = "Превышен лимит запросов";
                        isWarning = true;
                        break;
                    case 500:
                        message = "Внутренняя ошибка сервера";
                        title = "Ошибка сервера";
                        break;
                    case 502:
                    case 503:
                    case 504:
                        message = "Сервис временно недоступен. Попробуйте позже";
                        title = "Сервис недоступен";
                        break;
                    default:
                        message = ((_a = apiError.data) === null || _a === void 0 ? void 0 : _a.message) || apiError.message || message;
                }
            }
            else if (error && typeof error === "object" && "message" in error) {
                var errorWithMessage = error;
                // Обработка сетевых ошибок
                if (errorWithMessage.message.includes("Network Error") ||
                    errorWithMessage.message.includes("fetch")) {
                    message = "Проблемы с сетевым соединением";
                    title = "Ошибка сети";
                }
                else {
                    message = errorWithMessage.message;
                }
            }
            // Показываем предупреждение для менее критичных ошибок
            if (isWarning) {
                showWarning({
                    title: title,
                    message: message,
                });
            }
            else {
                showError({
                    title: title,
                    message: message,
                    showRetry: context === null || context === void 0 ? void 0 : context.retryable,
                    onRetry: context === null || context === void 0 ? void 0 : context.retry,
                    autoHiding: (context === null || context === void 0 ? void 0 : context.retryable) ? 7000 : 5000, // Больше времени если есть кнопка повтора
                });
            }
        };
        // Слушаем общие события уведомлений через mitt
        var handleAppNotification = function (data) {
            var type = data.type, message = data.message, title = data.title, showRetry = data.showRetry, onRetry = data.onRetry;
            switch (type) {
                case "success":
                    showSuccess({ title: title, message: message });
                    break;
                case "error":
                    showError({ title: title, message: message, showRetry: showRetry, onRetry: onRetry });
                    break;
                case "warning":
                    showWarning({ title: title, message: message });
                    break;
                case "info":
                    showInfo({ title: title, message: message });
                    break;
                default:
                    console.warn("Unknown notification type:", type);
            }
        };
        // Подписываемся на события через mitt
        eventBus.on("apiError", handleApiError);
        eventBus.on("appNotification", handleAppNotification);
        return function () {
            // Отписываемся от событий при размонтировании
            eventBus.off("apiError", handleApiError);
            eventBus.off("appNotification", handleAppNotification);
        };
    }, [showError, showWarning, showInfo, showSuccess]);
    return _jsx(_Fragment, { children: children });
};
/**
 * Утилитарная функция для отправки события ошибки API через mitt
 */
export var dispatchApiError = function (error, context) {
    eventBus.emit("apiError", { error: error, context: context });
};
/**
 * Утилитарные функции для быстрого показа уведомлений
 * Используют mitt event bus для глобального управления уведомлениями
 */
export var NotificationUtils = {
    /**
     * Показать уведомление об успешном выполнении операции
     */
    showSuccess: function (message, title) {
        eventBus.emit("appNotification", {
            type: "success",
            message: message,
            title: title || "Успешно",
        });
    },
    /**
     * Показать уведомление об ошибке
     */
    showError: function (message, title, options) {
        eventBus.emit("appNotification", __assign({ type: "error", message: message, title: title || "Ошибка" }, options));
    },
    /**
     * Показать предупреждение
     */
    showWarning: function (message, title) {
        eventBus.emit("appNotification", {
            type: "warning",
            message: message,
            title: title || "Предупреждение",
        });
    },
    /**
     * Показать информационное сообщение
     */
    showInfo: function (message, title) {
        eventBus.emit("appNotification", {
            type: "info",
            message: message,
            title: title || "Информация",
        });
    },
};
